import { z } from 'zod';

// Form correction types
export const formFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  z.date(),
  z.null(),
  z.undefined(),
]);

export const formDataSchema = z.record(z.string(), formFieldValueSchema);

export const patternContextSchema = z.object({
  userId: z.string().optional(),
  formType: z.string().optional(),
  sessionId: z.string().optional(),
  timestamp: z.date().optional(),
}).catchall(z.union([z.string(), z.number(), z.date()]));

export const correctionPatternSchema = z.object({
  id: z.string(),
  formParentId: z.number(),
  fieldPath: z.string(),
  fieldType: z.string(),
  originalPattern: z.string(),
  correctedPattern: z.string(),
  patternContext: patternContextSchema,
  frequency: z.number(),
  confidenceScore: z.number(),
  lastUpdated: z.date(),
  createdAt: z.date(),
});

export const formInputDatasetSchema = z.object({
  id: z.string(),
  formParentId: z.number(),
  formVersionId: z.number(),
  formVersionString: z.string(),
  originalTranscript: z.string().optional(),
  aiParsedData: formDataSchema,
  userCorrectedData: formDataSchema,
  userId: z.bigint(),
  createdAt: z.date(),
});

export const formInputCorrectionSchema = z.object({
  id: z.string(),
  formInputDatasetId: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  fieldPath: z.string(),
  originalValue: formFieldValueSchema,
  correctedValue: formFieldValueSchema,
  confidenceScore: z.number().optional(),
  correctionType: z.enum(['transcription_error', 'mapping_error', 'validation_error', 'no_change']).optional(),
  createdAt: z.date(),
});

export const correctionSuggestionSchema = z.object({
  field: z.string(),
  original: formFieldValueSchema,
  suggested: formFieldValueSchema,
  confidence: z.number(),
  reason: z.string(),
});

export const fieldCorrectionSchema = z.object({
  field: z.string(),
  original: formFieldValueSchema,
  corrected: formFieldValueSchema,
  type: z.enum(['transcription_error', 'mapping_error', 'validation_error', 'no_change']),
  confidence: z.number(),
});

export const correctionSummarySchema = z.object({
  corrections: z.array(fieldCorrectionSchema),
  totalCorrections: z.number(),
  confidenceScore: z.number(),
  autoCorrectedCount: z.number(),
  manualCorrectionsCount: z.number(),
});

export const patternMatchSchema = z.object({
  pattern: correctionPatternSchema,
  confidence: z.number(),
  matchType: z.enum(['exact', 'partial', 'fuzzy']),
});

export const appliedCorrectionSchema = z.object({
  field: z.string(),
  original: formFieldValueSchema,
  corrected: formFieldValueSchema,
  patternId: z.string(),
  confidence: z.number(),
});

export const createFormInputDatasetRequestSchema = z.object({
  formParentId: z.number(),
  formVersionId: z.number(),
  formVersionString: z.string(),
  originalTranscript: z.string().optional(),
  aiParsedData: formDataSchema,
  userCorrectedData: formDataSchema,
  userId: z.bigint(),
});

export const createFormInputCorrectionRequestSchema = z.object({
  formInputDatasetId: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  fieldPath: z.string(),
  originalValue: formFieldValueSchema,
  correctedValue: formFieldValueSchema,
  confidenceScore: z.number().optional(),
  correctionType: z.enum(['transcription_error', 'mapping_error', 'validation_error', 'no_change']).optional(),
});

export const createCorrectionPatternRequestSchema = z.object({
  formParentId: z.number(),
  fieldPath: z.string(),
  fieldType: z.string(),
  originalPattern: z.string(),
  correctedPattern: z.string(),
  patternContext: patternContextSchema.optional(),
});

export const formCorrectionStatsSchema = z.object({
  totalCorrections: z.number(),
  correctionsByVersion: z.record(z.string(), z.number()),
  averageCorrectionsPerForm: z.number(),
  mostCorrectedFields: z.array(z.object({
    field: z.string(),
    correctionCount: z.number(),
  })),
});

export const correctionPatternStatsSchema = z.object({
  totalPatterns: z.number(),
  patternsByType: z.record(z.string(), z.number()),
  mostFrequentPatterns: z.array(z.object({
    pattern: z.string(),
    frequency: z.number(),
    field: z.string(),
  })),
});

// Types
export type FormFieldValue = z.infer<typeof formFieldValueSchema>;
export type FormData = z.infer<typeof formDataSchema>;
export type PatternContext = z.infer<typeof patternContextSchema>;
export type CorrectionPattern = z.infer<typeof correctionPatternSchema>;
export type FormInputDataset = z.infer<typeof formInputDatasetSchema>;
export type FormInputCorrection = z.infer<typeof formInputCorrectionSchema>;
export type CorrectionSuggestion = z.infer<typeof correctionSuggestionSchema>;
export type FieldCorrection = z.infer<typeof fieldCorrectionSchema>;
export type CorrectionSummary = z.infer<typeof correctionSummarySchema>;
export type PatternMatch = z.infer<typeof patternMatchSchema>;
export type AppliedCorrection = z.infer<typeof appliedCorrectionSchema>;
export type CreateFormInputDatasetRequest = z.infer<typeof createFormInputDatasetRequestSchema>;
export type CreateFormInputCorrectionRequest = z.infer<typeof createFormInputCorrectionRequestSchema>;
export type CreateCorrectionPatternRequest = z.infer<typeof createCorrectionPatternRequestSchema>;
export type FormCorrectionStats = z.infer<typeof formCorrectionStatsSchema>;
export type CorrectionPatternStats = z.infer<typeof correctionPatternStatsSchema>;

export type CorrectionType = 'transcription_error' | 'mapping_error' | 'validation_error' | 'no_change';
